var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { createPortal } from 'react-dom';
import { createUseStyles } from 'react-jss';
import { useTheme, ThemeProvider } from '../../theme';
import { useMemoClasses, usePortalContainer, useRenderTransition } from '../../utils/hooks';
var useStyles = createUseStyles({
    container: {
        pointerEvents: 'none',
        position: 'fixed',
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
        width: '100vw',
        height: '100vh',
        left: 0,
        top: 0,
        overflow: 'hidden',
        zIndex: 100,
    },
    containerOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Drawer.container));
    },
    contained: {
        position: 'absolute',
        width: '100%',
        height: '100%',
    },
    base: {
        pointerEvents: 'all',
        position: 'absolute',
        backgroundColor: 'var(--RUI-color-paper)',
        padding: 'var(--RUI-padding)',
        boxShadow: '4px 4px 8px -4px black',
        boxSizing: 'border-box',
        borderRadius: 'var(--RUI-border-radius)',
        textAlign: 'left',
        opacity: 0,
        overflow: 'auto',
        transition: function (_a) {
            var transitionDuration = _a.transitionDuration;
            return "all ".concat(transitionDuration, "s");
        },
    },
    baseOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Drawer.base));
    },
    open: {
        opacity: 1,
    },
    openOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Drawer.open));
    },
    left: {
        left: '-100%',
        top: 0,
        height: '100%',
        maxWidth: '100%',
        width: 'fit-content',
        boxShadow: '2px 0px 8px 0px rgba(0,0,0,0.5)',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        '&$open': {
            left: 0,
        },
    },
    leftOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Drawer.left));
    },
    right: {
        right: '-100%',
        top: 0,
        height: '100%',
        maxWidth: '100%',
        width: 'fit-content',
        boxShadow: '-2px 0px 8px 0px rgba(0,0,0,0.5)',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        '&$open': {
            right: 0,
        },
    },
    rightOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Drawer.right));
    },
    top: {
        top: '-100%',
        left: 0,
        maxHeight: '100%',
        height: 'fit-content',
        width: '100%',
        boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.5)',
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        '&$open': {
            top: 0,
        },
    },
    topOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Drawer.top));
    },
    bottom: {
        bottom: '-100%',
        left: 0,
        maxHeight: '100%',
        height: 'fit-content',
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: '100%',
        '&$open': {
            bottom: 0,
        },
    },
    bottomOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Drawer.bottom));
    },
    backdrop: {
        pointerEvents: 'all',
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.5)',
        boxSizing: 'border-box',
    },
    backdropOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Drawer.backdrop));
    },
}, { name: 'RUIDrawer' });
var Drawer = function (_a) {
    var anchor = _a.anchor, children = _a.children, open = _a.open, backdrop = _a.backdrop, contained = _a.contained, transitionDuration = _a.transitionDuration, persistent = _a.persistent, onBackdropClick = _a.onBackdropClick, className = _a.className, props = __rest(_a, ["anchor", "children", "open", "backdrop", "contained", "transitionDuration", "persistent", "onBackdropClick", "className"]);
    var theme = useTheme();
    var styles = useStyles({ theme: theme, transitionDuration: transitionDuration });
    var _b = useRenderTransition(open, transitionDuration * 1000), shouldRender = _b.shouldRender, shouldApplyTransition = _b.shouldApplyTransition;
    var containerClasses = useMemoClasses([
        styles.container,
        theme.overrides.Drawer.container && styles.containerOverrides,
        contained && styles.contained,
        'RUIDrawer-container',
    ], [theme, contained]);
    var backdropClasses = useMemoClasses([styles.backdrop, theme.overrides.Drawer.backdrop && styles.backdropOverrides, 'RUIDrawer-backdrop'], [theme]);
    var drawerClasses = useMemoClasses([
        styles.base,
        theme.overrides.Drawer.base && styles.baseOverrides,
        shouldApplyTransition && styles.open,
        shouldApplyTransition && theme.overrides.Drawer.open && styles.openOverrides,
        anchor === 'left' && styles.left,
        anchor === 'left' && theme.overrides.Drawer.left && styles.leftOverrides,
        anchor === 'right' && styles.right,
        anchor === 'right' && theme.overrides.Drawer.right && styles.rightOverrides,
        anchor === 'top' && styles.top,
        anchor === 'top' && theme.overrides.Drawer.top && styles.topOverrides,
        anchor === 'bottom' && styles.bottom,
        anchor === 'bottom' && theme.overrides.Drawer.bottom && styles.bottomOverrides,
        'RUIDrawer-base',
        className,
    ], [theme, anchor, className, shouldApplyTransition]);
    var drawerTree = shouldRender || persistent ? (React.createElement("div", { className: containerClasses },
        backdrop && open && React.createElement("span", { className: backdropClasses, onClick: function () { return onBackdropClick(); } }),
        React.createElement("div", __assign({ className: drawerClasses }, props), children))) : null;
    // render inside a portal if required
    var container = usePortalContainer('Drawer', !contained);
    if (container && drawerTree)
        return createPortal(React.createElement(ThemeProvider, { theme: theme }, drawerTree), container);
    return drawerTree;
};
Drawer.defaultProps = {
    anchor: 'left',
    open: false,
    onBackdropClick: function () { },
    className: '',
    backdrop: true,
    contained: false,
    persistent: false,
    transitionDuration: 0.3,
};
export { Drawer as default, Drawer };
